import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="geolocation"
export default class extends Controller {
  static targets = [
    "latitude", 
    "longitude", 
    "accuracy", 
    "status", 
    "consent", 
    "consentCheckbox",
    "consentBox",
    "submitButton",
    "statusMessage",
    "loadingMessage",
    "successMessage",
    "errorMessage"
  ]
  static values = {
    required: { type: Boolean, default: false },
    autoCapture: { type: Boolean, default: false }
  }

  connect() {
    console.log("Geolocation controller connected")
    
    // Auto-capture location if enabled
    if (this.autoCaptureValue) {
      this.requestLocation()
    }

    // Check if geolocation is supported
    if (!this.isGeolocationSupported()) {
      this.showError(t("javascript.controllers.geolocation_controller.not_supported_by_browser"))
      if (this.requiredValue) {
        this.disableSubmit()
      }
    }
  }

  // Toggle consent and handle location request
  toggleConsent(event) {
    if (this.hasConsentCheckboxTarget && this.consentCheckboxTarget.checked) {
      this.requestLocation()
    } else {
      // Clear location fields if consent is unchecked
      if (this.hasLatitudeTarget) this.latitudeTarget.value = ""
      if (this.hasLongitudeTarget) this.longitudeTarget.value = ""
      if (this.hasAccuracyTarget) this.accuracyTarget.value = ""
      this.hideStatus()
    }
  }

  // Capture location on form submit (if consent is granted)
  captureLocation(event) {
    // If consent checkbox exists and is checked, ensure location is captured
    if (this.hasConsentCheckboxTarget && this.consentCheckboxTarget.checked) {
      // If location hasn't been captured yet, prevent submit and request it
      if (this.hasLatitudeTarget && !this.latitudeTarget.value) {
        event.preventDefault()
        this.showLoading()
        this.requestLocation().then(() => {
          // Submit the form after location is captured
          this.submitForm(event.target)
        }).catch(() => {
          // If location fails but it's not required, allow form submission
          if (!this.requiredValue) {
            this.submitForm(event.target)
          }
        })
      }
    }
  }

  // Helper method to submit form with proper Turbo support
  submitForm(form) {
    // Use requestSubmit() if available (modern browsers) for proper event triggering
    // Falls back to submit() for older browsers
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }

  // Request geolocation permission and capture location
  requestLocation(event) {
    if (event) {
      event.preventDefault()
    }

    return new Promise((resolve, reject) => {
      // Check consent if consent target exists
      if (this.hasConsentCheckboxTarget && !this.consentCheckboxTarget.checked) {
        this.showError(t("javascript.controllers.geolocation_controller.grant_permission"))
        reject(new Error("Consent not granted"))
        return
      }

      this.showLoading()

      if (!this.isGeolocationSupported()) {
        this.showError(t("javascript.controllers.geolocation_controller.not_supported"))
        reject(new Error("Geolocation not supported"))
        return
      }

      const options = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.onSuccess(position)
          resolve(position)
        },
        (error) => {
          this.onError(error)
          reject(error)
        },
        options
      )
    })
  }

  // Success callback
  onSuccess(position) {
    console.log("Location captured:", position.coords)

    // Set hidden form fields
    if (this.hasLatitudeTarget) {
      this.latitudeTarget.value = position.coords.latitude
    }
    if (this.hasLongitudeTarget) {
      this.longitudeTarget.value = position.coords.longitude
    }
    if (this.hasAccuracyTarget) {
      this.accuracyTarget.value = position.coords.accuracy
    }

    this.showSuccess()

    // Enable submit button if it was disabled
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }

    // Dispatch custom event for other controllers to listen to
    this.dispatch("captured", {
      detail: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      }
    })
  }

  // Error callback
  onError(error) {
    console.error("Geolocation error:", error)

    let message = t("javascript.controllers.geolocation_controller.unable_to_retrieve")
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        message = t("javascript.controllers.geolocation_controller.permission_denied")
        break
      case error.POSITION_UNAVAILABLE:
        message = t("javascript.controllers.geolocation_controller.position_unavailable")
        break
      case error.TIMEOUT:
        message = t("javascript.controllers.geolocation_controller.timeout")
        break
    }

    this.showError(message)

    // If location is required, keep submit disabled
    if (this.requiredValue && this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    } else {
      // If not required, uncheck consent and allow submission without location
      if (this.hasConsentCheckboxTarget) {
        this.consentCheckboxTarget.checked = false
      }
    }
  }

  // Show loading state
  showLoading() {
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.classList.remove("hidden")
    }
    if (this.hasLoadingMessageTarget) {
      this.loadingMessageTarget.classList.remove("hidden")
    }
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add("hidden")
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  // Show success state
  showSuccess() {
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.classList.remove("hidden")
    }
    if (this.hasLoadingMessageTarget) {
      this.loadingMessageTarget.classList.add("hidden")
    }
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.remove("hidden")
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  // Show error state
  showError(message) {
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.classList.remove("hidden")
    }
    if (this.hasLoadingMessageTarget) {
      this.loadingMessageTarget.classList.add("hidden")
    }
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add("hidden")
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.remove("hidden")
    }
  }

  // Hide all status messages
  hideStatus() {
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.classList.add("hidden")
    }
    if (this.hasLoadingMessageTarget) {
      this.loadingMessageTarget.classList.add("hidden")
    }
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add("hidden")
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  // Check if geolocation is supported
  isGeolocationSupported() {
    return "geolocation" in navigator
  }

  // Disable submit button
  disableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
  }

  // Handle consent change (legacy support)
  consentChanged() {
    this.toggleConsent()
  }
}
